import { type StructuredContentDocument } from '@repo/contracts/multi-use/structured-content/types';
import { css } from '@repo/pandacss/styled-system/css';
import { type CSSProperties } from 'react';
import { renderStructuredContent } from '../../rendererContext';
import type { RendererProps } from '../../types';
import { BORDER_RADIUS_MAP, COLORS_MAP, SPACING_TOKEN_MAP } from '../constants';
import type { BorderRadiusToken, ColorToken, SpacingToken } from '../types';
import type { ColumnWidth, WidthObject } from './types';
import { isWidthObject } from './utils';

type ColumnProps = StructuredContentDocument<{
  backgroundColor?: ColorToken;
  borderRadius?: BorderRadiusToken;
  children: StructuredContentDocument;
  collapse?: boolean;
  columnStart?: ColumnWidth;
  equalHeight?: boolean;
  order?: { desktop?: number; mobile?: number };
  padding?: SpacingToken;
  width?: WidthObject | ColumnWidth;
  rendererProps?: RendererProps;
}>;

export const Column = ({ props }: ColumnProps) => {
  const {
    backgroundColor,
    borderRadius,
    children,
    collapse = false,
    columnStart,
    equalHeight,
    order,
    padding,
    width,
    rendererProps,
  } = props;

  const mobileWidth = isWidthObject(width) ? width.mobile : (width ?? '12');
  const desktopWidth = isWidthObject(width) ? width.desktop : (width ?? '12');

  return (
    <div
      className={css({
        height: 'var(--column-height)',
        paddingTop: 'var(--column-padding)',
        paddingRight: 'var(--column-padding)',
        paddingBottom: 'var(--column-padding)',
        paddingLeft: 'var(--column-padding)',
        borderTopLeftRadius: 'var(--column-border-radius)',
        borderTopRightRadius: 'var(--column-border-radius)',
        borderBottomRightRadius: 'var(--column-border-radius)',
        borderBottomLeftRadius: 'var(--column-border-radius)',
        backgroundColor: 'var(--column-background-color)',
        gridColumnStart: 'var(--column-grid-column-start)',
        order: { base: 'var(--column-order-xs)', md: 'var(--column-order-md)' },
        gridColumnEnd: {
          base: 'var(--column-grid-column-end-xs)',
          sm: 'var(--column-grid-column-end-sm)',
        },
      })}
      style={
        {
          '--column-height': equalHeight ? '100%' : undefined,
          '--column-padding': SPACING_TOKEN_MAP[padding || 0],
          '--column-border-radius': BORDER_RADIUS_MAP[borderRadius || 0],
          '--column-background-color': COLORS_MAP[backgroundColor ?? 'white'],
          '--column-grid-column-start': collapse ? 'unset' : (columnStart ?? undefined),
          '--column-order-xs': order ? order.mobile : undefined,
          '--column-order-md': order ? order.desktop : undefined,
          '--column-grid-column-end-xs': collapse ? undefined : `span ${Number(mobileWidth)}`,
          '--column-grid-column-end-sm': `span ${Number(desktopWidth)}`,
        } as CSSProperties
      }
    >
      {renderStructuredContent(children, rendererProps)}
    </div>
  );
};
